import type { ComponentType } from 'react'
import { lazy, Suspense, useMemo } from 'react'
import { List, ListItemText, ListItemButton } from '@mui/material'
import LockOutlined from '@mui/icons-material/LockOutlined'
import { NavLink, Route, Routes } from 'react-router'
import {
  BehindProviderFeatureAccessCheck,
  EmptyState,
  ErrorScreen,
  InterfaceContent,
  Loader,
} from '~/shared/ui'
import { ErrorBoundary } from 'react-error-boundary'
import { Features } from '~/shared/types'
import { useFeatureAccess } from '~/shared/hooks/useFeatureAccess'
import { useFeatureSwitches } from '~/shared/hooks/useFeatureSwitch'
// Small settings components are imported directly since Settings screen is already lazy-loaded
// This reduces the number of tiny chunks while maintaining code splitting at the route level
import MacroSettings from '~/features/settings/macros'
import EmpanelmentSettings from '~/features/settings/empanelment'
import PracticeHoursSettings from '~/features/settings/practice-hours'
import PracticeSuiteInvoiceSettings from '~/features/settings/practice-suite-invoice'
import CareTeamReassignmentSettings from '~/features/settings/care-team-reassignment'
import TeamsCoverageSettings from '~/features/settings/teams-coverage'
import BenefitPolicySettings from '~/features/settings/benefit-policies'

// ProviderShiftSettings is larger (36KB) so keep it lazy-loaded
const ProviderShiftSettings = lazy(() => import('~/features/settings/provider-shifts'))

interface NavLinkConfig {
  path: string
  name: string
  component: ComponentType
  enabled: boolean
  feature: Features
  // When set, the link and its route appear only while the named Waffle switch is active.
  featureSwitch?: string
}

const NAV_LINKS: NavLinkConfig[] = [
  {
    path: 'provider-shifts',
    name: 'Provider Shifts',
    component: ProviderShiftSettings,
    enabled: true,
    feature: 'practice_settings',
  },
  {
    path: 'clinic-hours',
    name: 'Practice Hours',
    component: PracticeHoursSettings,
    enabled: true,
    feature: 'practice_settings',
  },
  {
    path: 'empanelment',
    name: 'State Licenses',
    component: EmpanelmentSettings,
    enabled: true,
    feature: 'practice_settings',
  },
  {
    path: 'macros',
    name: 'Macros',
    component: MacroSettings,
    enabled: true,
    feature: 'practice_settings',
  },
  {
    path: 'practice-suite',
    name: 'Practice Suite Invoice Upload',
    component: PracticeSuiteInvoiceSettings,
    enabled: true,
    feature: 'billing',
  },
  {
    path: 'care-team-reassignment',
    name: 'Care Team Reassignment',
    component: CareTeamReassignmentSettings,
    enabled: true,
    feature: 'practice_settings',
  },
  {
    path: 'teams-coverage',
    name: 'Teams & Coverage',
    component: TeamsCoverageSettings,
    enabled: true,
    feature: 'practice_settings',
  },
  {
    path: 'benefit-policies',
    name: 'Benefit Policies',
    component: BenefitPolicySettings,
    enabled: true,
    feature: 'practice_settings',
    featureSwitch: 'benefit_policy_settings',
  },
]

const Settings = () => {
  const featureAccess = useFeatureAccess()
  const featureSwitches = useFeatureSwitches().data?.featureSwitchesByName

  // A link is listed only when its route will actually render: same feature-access
  // check the route wrapper applies, plus the link's own switch.
  const links = useMemo(
    () =>
      NAV_LINKS.filter(
        link =>
          link.enabled &&
          featureAccess?.tenant?.[link.feature] === true &&
          (link.featureSwitch ? !!featureSwitches?.[link.featureSwitch]?.active : true),
      ),
    [featureAccess, featureSwitches],
  )

  const sidebar = (
    <List>
      {links.map(link => (
        <ListItemButton
          component={NavLink}
          to={`/settings/${link.path}`}
          key={link.path}
          sx={{
            '&.active': {
              color: 'primary.main',
            },
          }}
        >
          <ListItemText primary={link.name} sx={{ '& span': { fontSize: '1.8rem' } }} />
        </ListItemButton>
      ))}
    </List>
  )

  return (
    <InterfaceContent sidebar={sidebar}>
      <ErrorBoundary FallbackComponent={ErrorScreen}>
        {featureAccess && links.length === 0 && (
          <EmptyState
            icon={<LockOutlined />}
            title="No settings available"
            caption="Your practice does not have access to any settings pages. Reach out to your administrator if you expected to manage settings here."
          />
        )}
        <Suspense fallback={<Loader />}>
          <Routes>
            {links.map(link => {
              const LinkComponent = link.component
              return (
                <Route
                  key={link.path}
                  path={`${link.path}/*`}
                  element={
                    <BehindProviderFeatureAccessCheck
                      featureAccess={featureAccess}
                      feature={link.feature}
                    >
                      <LinkComponent />
                    </BehindProviderFeatureAccessCheck>
                  }
                />
              )
            })}
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </InterfaceContent>
  )
}

export default Settings
